import { useRef, FocusEvent, useState } from 'react';

import Search from '@legacy/components/layout/Header/components/MainSearch/components/Search';
import legacyStyles from '@legacy/components/layout/Header/components/MainSearch/MainSearch.module.scss';
import { useSearchKeyboardEvent } from '@legacy/components/layout/Header/components/MainSearch/MainSearchHookHelper';
import scssVariables from '@styles/variables.module.scss';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './LegacyMainSearch.module.scss';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import { useGetSearchCategory } from '@/legacy/apis/searchAPI/categoriesAPI';
import { DefaultLink } from '@/legacy/components/global/DefaultLink';
import { LinkProvider } from '@/legacy/components/global/LinkProvider';
import { useSearchStore } from '@/stores/search';

export const LegacyMainSearch = () => {
  const MainSearchRef = useRef<HTMLDivElement>(null);
  const MainSearchTriggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFilterRef = useRef<HTMLDivElement>(null);
  const [selectItemIndex, setSelectItemIndex] = useState<number>(-1);
  const [searchValue, setSearchValue] = useState<string>('');

  const { isMainSearchOpen, updateStore } = useSearchStore();

  // use this to check if user has access to search functionality
  const { isSearchCategoryListEmpty } = useGetSearchCategory();
  const userHasAccessToSearch = !isSearchCategoryListEmpty;

  const closeSearch = () => {
    setSelectItemIndex(-1);
    updateStore({
      type: 'RESET_SEARCH',
    });
  };

  const toggleSearch = () =>
    updateStore({
      type: 'TOGGLE_SEARCH',
    });

  const handleClick = () => {
    setTimeout(() => {
      searchInputRef?.current?.focus();
    }, 10);
    return toggleSearch();
  };

  const onBlurHandle = ({
    target,
    currentTarget,
    relatedTarget,
  }: FocusEvent<HTMLDivElement>) => {
    const isSearchIconButton = MainSearchTriggerRef?.current === relatedTarget;
    const isNotSelfTrigger = currentTarget !== target;
    const isNotChildEvent = !currentTarget?.contains(relatedTarget as Node);

    if (isSearchIconButton) {
      return;
    }

    if (isNotSelfTrigger && isNotChildEvent) {
      closeSearch();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const relatedTargetId = (relatedTarget as any)?.getAttribute(
        'data-automation-id',
      );

      if (relatedTargetId === 'TopBarSearchContent') {
        searchInputRef?.current?.focus();
      } else if (relatedTargetId === 'MainSearchCategoryPanel') {
        searchFilterRef?.current?.focus();
      }
    }
  };

  useSearchKeyboardEvent(
    isMainSearchOpen,
    closeSearch,
    MainSearchRef.current,
    MainSearchTriggerRef,
  );

  const { t } = useTranslation('header');

  return userHasAccessToSearch ? (
    // added LinkProvider to have its context available in the search results
    // this makes the search result use a router link
    <LinkProvider value={DefaultLink}>
      <div className={legacyStyles.Wrapper} ref={MainSearchRef}>
        <IconButton
          size="small"
          ref={MainSearchTriggerRef}
          aria-controls="main-search"
          aria-expanded={isMainSearchOpen}
          selected={isMainSearchOpen}
          onClick={handleClick}
          color="secondary"
          data-automation-id="TopBarSearchButton"
          iconName={uiIcons.actions.search}
          tooltip={t('SearchButton')}
          iconProps={{ color: scssVariables.ColorBlack100 }}
        />

        <div
          id="main-search"
          className={cx(legacyStyles.PopOut, styles.MainSearchPopupNewScope)}
          hidden={!isMainSearchOpen}
          tabIndex={-1}
          onBlur={onBlurHandle}
          data-automation-id="TopBarSearchContent"
        >
          <Search
            inputRef={searchInputRef}
            searchFilterRef={searchFilterRef}
            selectItemIndex={selectItemIndex}
            setSelectItemIndex={setSelectItemIndex}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </div>
      </div>
    </LinkProvider>
  ) : null;
};
